import React, { ReactNode } from 'react';
import { Modifier } from '../../../lib/bemUtils/Modifier';
import { AxiosError, AxiosResponse } from 'axios';
import { useTranslation } from 'react-i18next';
import { StatusInfoLoading } from '../../components/StatusInfo/StatusInfoLoading';
import { StatusInfoPositiveCheck } from '../../components/StatusInfo/StatusInfoPositiveCheck';
import { StatusInfoError, StatusInfoErrorWithRequestId } from '../../components/StatusInfo';
import { StatusInfoModifier } from '../../components/StatusInfo/partials/StatusInfo';
import LiveRegion from '../../../components/atoms/LiveRegion/LiveRegion';
import { getRequestIdFromHeaders } from '../../../lib/HeaderUtils/RequestID';
import { SonResponseError, SonResponseErrorTypes } from '../types/SonResponseError';
import { FetchState } from '../../../hooks/reducers/useFetchReducer';
import { getDefaultErrorTypeTranslations } from '../utils/defaultErrorTypeTranslations';
import { translatePartOfStringWithLink } from '../../../lib/StringUtils/StringHTMLUtils';
import { useAppContext } from '../../../contexts/AppContext/AppContext';

export type DefaultStatusMessages = {
  messageSuccess: ReactNode;
  messageForward?: string | undefined;
  messageSending: ReactNode;
  messageFail: ReactNode;
};

/**
 * Custom Error Type Message for overriding errorTypes for specific forms.
 */
export type CustomErrorTypeMessages = {
  customErrorTypeMessages?: Partial<Record<SonResponseErrorTypes, string>>;
};

export type StatusMessages<T, TError = AxiosError<SonResponseError>> = DefaultStatusMessages &
  CustomErrorTypeMessages & {
    closePreventedMessage?: string | null;
    customErrorResponseMapper?: (errorResponse: AxiosResponse<any>) => ReactNode | undefined;
    fetchState: FetchState<T, TError>;
    successModifier?: Modifier<StatusInfoModifier>;
    sendingModifier?: Modifier<StatusInfoModifier>;
    errorModifier?: Modifier<StatusInfoModifier>;
  };

/**
 * Checks whether the given value has the shape of an AxiosError. StatusMessages
 * treats non-Axios errors (e.g. the string requestError of useGet) as simple
 * failures without error type mapping or request id.
 */
const isAxiosErrorLike = (error: unknown): error is AxiosError<SonResponseError> => {
  return typeof error === 'object' && error !== null && 'status' in error;
};

/**
 * Checks whether the given value has the shape of an AxiosResponse. Used to
 * guard access to the response body (e.g. Blob responses have no body data).
 */
const isAxiosResponseLike = (response: unknown): response is AxiosResponse => {
  return typeof response === 'object' && response !== null && 'data' in response;
};

export function StatusMessages<T, TError = AxiosError<SonResponseError>>({
  messageSuccess,
  messageForward,
  messageSending,
  messageFail,
  customErrorTypeMessages,
  fetchState,
  customErrorResponseMapper,
  closePreventedMessage,
  successModifier = [StatusInfoModifier.PADDING_TOP, StatusInfoModifier.RIGHT],
  sendingModifier = [StatusInfoModifier.PADDING_TOP, StatusInfoModifier.RIGHT],
  errorModifier = [StatusInfoModifier.PADDING_TOP, StatusInfoModifier.RED, StatusInfoModifier.RIGHT],
}: StatusMessages<T, TError>) {
  const { t } = useTranslation();
  const { portalConfiguration } = useAppContext();

  /**
   * Get custom messages for specific error types.
   */
  const getCustomDisplayableErrorMessage = (type: SonResponseErrorTypes): string | undefined => {
    return customErrorTypeMessages ? customErrorTypeMessages[type] : undefined;
  };

  const getFailMessage = (err: TError): ReactNode => {
    const axiosError = err as AxiosError<SonResponseError>;
    const errorType = axiosError?.response?.data?.errorType;

    if (errorType) {
      return (
        getCustomDisplayableErrorMessage(errorType) ||
        getDefaultErrorTypeTranslations(t, axiosError?.response?.data, portalConfiguration)
      );
    }

    return messageFail;
  };

  // for easier testing we provide every status with a data-form-status attribute
  const liveRegionMessage = () => {
    switch (fetchState.type) {
      case 'loading':
        return <StatusInfoLoading modifier={sendingModifier} message={messageSending} dataFormStatus="sending" />;
      case 'success': {
        const response = fetchState.response as AxiosResponse<{ url?: string }> | undefined;
        const forwardUrl = isAxiosResponseLike(response) ? response?.data?.url : undefined;
        return (
          <>
            <StatusInfoPositiveCheck modifier={successModifier} message={messageSuccess} dataFormStatus="success" />
            {messageForward && forwardUrl ? (
              <StatusInfoLoading
                modifier={successModifier}
                // Use window location (withRouting=false) to ensure full page reload, closing the modal
                // This matches the automatic redirect behavior in useRedirect hook
                message={translatePartOfStringWithLink(messageForward, forwardUrl, false)}
                dataFormStatus="forwarding"
              />
            ) : null}
          </>
        );
      }
      case 'error': {
        const requestError = fetchState.requestError;
        if (isAxiosErrorLike(requestError)) {
          const isClientError = requestError.status && requestError.status >= 400 && requestError.status < 500;

          if (isClientError) {
            return (
              <StatusInfoError
                modifier={errorModifier}
                message={getFailMessage(requestError)}
                additionalContent={customErrorResponseMapper?.(requestError?.response as any)}
                dataFormStatus="fail"
              />
            );
          } else {
            return (
              <StatusInfoErrorWithRequestId
                modifier={errorModifier}
                role="alert"
                message={getFailMessage(requestError)}
                additionalContent={customErrorResponseMapper?.(requestError?.response as any)}
                requestId={getRequestIdFromHeaders(requestError?.response?.headers)}
                dataFormStatus="fail"
              />
            );
          }
        }
        return (
          <StatusInfoError modifier={errorModifier} message={getFailMessage(requestError)} dataFormStatus="fail" />
        );
      }
      default:
        return null;
    }
  };

  return (
    <LiveRegion htmlTag="div" invisible={false} ariaAtomic>
      {liveRegionMessage()}
      {closePreventedMessage && <StatusInfoError message={closePreventedMessage} modifier={StatusInfoModifier.RIGHT} />}
    </LiveRegion>
  );
}
